import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import localeService, { Locale } from '@/services/localeService';
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';
import { logError } from '@/utils/errorHandler';

interface LocaleFilterProps {
  value: string | undefined;
  onChange: (value: string) => void;
}

const LocaleFilter: React.FC<LocaleFilterProps> = ({ value, onChange }) => {
  // Get translations
  const i18n = useReportspageTranslations();

  const {
    data: locales,
    isLoading,
    error,
  } = useQuery<Locale[]>({
    queryKey: ['locales', 'analytics'],
    queryFn: localeService.getLocales,
  });

  // Create a lookup map for locale display names
  const localeNameMap = React.useMemo(() => {
    if (!locales) return {};

    return locales.reduce(
      (acc: Record<string, string>, locale: Locale) => {
        acc[locale.code] = locale.label || locale.description || locale.code;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [locales]);

  if (isLoading) {
    return <Skeleton className="h-10 w-[200px]" />;
  }

  if (error) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      component: 'LocaleFilter',
      action: 'loadLocales',
      metadata: {
        context: 'locale_filter_loading_failure',
      },
    });
  }

  return (
    <div className="flex flex-col space-y-1">
      <label htmlFor="locale-filter" className="text-sm font-medium">
        {i18n.filters.labels.locale}
      </label>
      <Select value={value ?? 'all'} onValueChange={onChange}>
        <SelectTrigger id="locale-filter" className="w-[200px]">
          <SelectValue placeholder={i18n.filters.placeholders.allLocales}>
            {value && value !== 'all'
              ? localeNameMap[value] || i18n.filters.placeholders.allLocales
              : i18n.filters.placeholders.allLocales}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{i18n.filters.placeholders.allLocales}</SelectItem>
          {locales && locales.length > 0 ? (
            locales.map((locale: Locale) => (
              <SelectItem key={locale.code} value={locale.code}>
                {locale.label || locale.description || locale.code}
              </SelectItem>
            ))
          ) : (
            <SelectItem value="no-locales" disabled>
              {i18n.filters.options.noLocales}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

export default LocaleFilter;
